<template>
  <div class="w-100 h-100">
    <b-container fluid>
      <h2 class="tw-mb-0 text-aegis-dk">
        Analysis Data
      </h2>
      <p class="tw-italic tw-text-gray-700">
        View/Download curated analysis data.
      </p>
    </b-container>

    <b-container fluid class="tw-my-5">
      <b-row>
        <b-col cols="4">
          <b-form-select size="lg" v-model="selected_view" :options="views" @change="resetPhase()"></b-form-select>
        </b-col>

        <b-col cols="6" />

        <b-col
          v-if="totalItems"
          cols="2"
          class="text-right"
        >
          <b-button
            size="lg"
            @click="downloadFile()"
            variant="secondary"
            target="_blank"
            class="px-5"
          >
            {{ csvLoading
                ? "Downloading..."
                : csvLoadingError
                  ? "Error"
                  : "Download"
            }}
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-container fluid>

      <b-alert v-if="loading" variant="info" show>Loading... <i class="fas fa-spin fa-spinner"></i></b-alert>
      <b-alert v-if="loadingError" variant="danger" show>Cannot load data <i class="fas fa-spin fa-spinner"></i></b-alert>

      <b-table
        ref="table"
        striped
        hover
        responsive
        bordered
        :items="data"
        :fields="fields"
        :busy.sync="loading"
        :current-page="options.currentPage"
        :per-page="options.perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-slot:cell(age_at_enrollment)="data">
          {{(Math.round(data.value * 100) / 100).toFixed(2)}}
        </template>
      </b-table>

    </b-container>

    <b-pagination
      v-if="totalItems"
      v-model="options.currentPage"
      :total-rows="options.totalRows"
      :per-page="options.perPage"
      align="right"
      size="sm"
      class="my-0"
    />
  </div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";

import * as _ from "lodash";

export default {
  mixins: [DashboardBaseTemplate],
  data() {
    return {
      options: {
        currentPage: 1,
        totalRows: 0,
        perPage: 30,
      },
      loading: false,
      csvLoading: false,
      loadingError: false,
      csvLoadingError: false,
      search: "",
      fields: [],
      data: [],
      sortBy: "cluster",
      sortDesc: false,
      title: "",
      totalItems: 0,
      stacked: false,
      selected_view: "D1 - Infection_Data",
      views: [
        {
          value: "D1 - Infection_Data",
          text: "D1 - Infection Data"
        },
        {
          value: "D2 - Product_Placement_Replacement",
          text: "D2 - Product Placement/Replacement"
        },
        {
          value: "D3 - Entomology_Data",
          text: "D3 - Entomology Data"
        },
        {
          value: "D5 - Safety",
          text: "D5 - Safety"
        },
        {
          value: "Subject_Participation",
          text: "Subject Participation"
        },
        {
          value: "LTFU_Summary",
          text: "LTFU Summary"
        },
        {
          value: "Subject_Case_Attributes",
          text: "Subject Case Attributes"
        },
        {
          value: "Household_Case_Attributes",
          text: "Household Case Attributes"
        }
      ],
      study_phases: [
        {value: 'all', text: 'All'},
        {value: 'eleven_days', text: '11 Day Wait'},
        {value: 'fourteen_days', text: '14 Day Wait'},
      ],
      selected_phase: "all"
    };
  },
  computed: {},
  watch: {
    selected_view: function() {
      this.find();
      this.options.currentPage = 1;
    },
    selected_phase: function() {
      this.find();
      this.options.currentPage = 1;
    }
  },
  created() {
    this.find();
  },
  methods: {
    resetPhase() {
      this.selected_phase = "all"
    },
    find() {
      const that = this;
      that.loading = true;

      // Special handling for D5 - Safety - just show empty table
      if (that.selected_view === "D5 - Safety") {
        that.loading = false;
        that.fields = [];
        that.data = [];
        that.options.totalRows = 0;
        that.totalItems = 1; // Set to 1 to show download button
        return;
      }

      let params = {
        analysis: that.selected_view,
      };

      params.study_phase = that.selected_phase;

      return HTTP.get("/eavetubes/analysis", {
        params: params
      })
      .then((response) => {
        that.loading = false;
        let fields = [];
        _.each(response.data.fields, function(field) {
          fields.push({
            key: field,
            sortable: true,
          });
        });
        that.fields = fields;

        that.data = response.data.data;
        that.options.totalRows = that.data.length;
        that.totalItems = that.data.length;
      })
      .catch((error) => {
        that.loading = false;
        that.loadingError = true;
        HandleErrors.HandleErrors(error);
        // set field and data to be empty
        that.fields = [];
        that.data = [];
        that.options.totalRows = that.data.length;
        that.totalItems = that.data.length;
      });
    },
    downloadFile: function() {
      const that = this;
      that.csvLoading = true;
      
      // Special handling for D5 - Safety
      if (that.selected_view === "D5 - Safety") {
        var params = new URLSearchParams();
        var request = {
          params: params,
          responseType: "arraybuffer",
        };

        HTTP.get(`/eavetubes/download-d5-safety-data`, request)
        .then((response) => {
          var fileURL = window.URL.createObjectURL(
            new Blob([response.data])
          );
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          var fileName = "ET-D5 - Safety_Data_updated-04-07-26.xlsx";
          fileLink.setAttribute("download", fileName);
          document.body.appendChild(fileLink);
          fileLink.click();
          that.csvLoading = false;
          // Cleanup
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(fileLink);
        })
        .catch((error) => {
          that.csvLoading = false;
          that.csvLoadingError = true;
          HandleErrors.HandleErrors(error);
        });
        return;
      }
      
      // Default behavior for all other selections
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/eavetubes/analysis?download=1&analysis=${that.selected_view}`;

      downloadUrl += `&study_phase=${that.selected_phase}`;

      HTTP.get(downloadUrl).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement('a');
        fileLink.href = fileURL;
        const now = new Date();
        fileLink.setAttribute('download', `ET_${that.selected_view}_Analysis_${now}.csv`);
        document.body.appendChild(fileLink);
        fileLink.click();
        that.csvLoading = false;
      }).catch((error) => {
        that.csvLoading = false;
        that.csvLoadingError = true;
        HandleErrors.HandleErrors(error);
      });
    }
  },
};
</script>

<style></style>
